import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { EventName } from './analytics.events';
import { Ga4Provider } from './ga4.provider';
import { SseProvider } from './sse.provider';
import { MixpanelProvider } from './mixpanel.provider';
import {
  AnalyticsProvider,
  EventProperties,
  UserIdentity,
  UserProperties,
  UserRole,
} from './analytics.types';


/**
 * Where the caller's role is remembered ACROSS PAGE LOADS.
 *
 * `currentRole` below is a plain field on a `providedIn: 'root'` service, so it
 * is rebuilt as 'anonymous' on every full page load. identify() is only called
 * from the six auth screens, and a logged-in user does not pass through those
 * again — so before this existed, every event after the first refresh was
 * tagged `actor_role: anonymous` while Mixpanel's own People profile still
 * said `role: customer`. The two disagreed, and the events were the wrong half.
 *
 * Mixpanel persists distinct_id itself; what it cannot persist is OUR notion of
 * the role, because that is ours. Hence this.
 */
const IDENTITY_KEY = 'sip_analytics_identity';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private providers: AnalyticsProvider[] = [];
  private initialized = false;
  private currentRole: UserRole = 'anonymous';
  private lastPath: string | null = null;

  constructor(
    private router: Router,
    private zone: NgZone,
  ) {}

  async init(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;

    // Subscribe to Router.events FIRST — before we await any async provider
    // load. The GA4 provider fetches gtag.js over the network; if that takes
    // even 50ms, Angular's initial NavigationEnd fires before we await
    // resolves, and RxJS Subjects don't replay past emissions. So we set up
    // the subscription synchronously, and each provider's own trackRoute()
    // is a safe no-op until its SDK is ready.
    // SseProvider last. forEachProvider isolates each with its own try/catch,
    // so order carries no correctness weight — but the two hosted SDKs are
    // what the business reports on today, and they should not queue behind
    // our own sink's setInterval registration.
    this.providers = [new MixpanelProvider(), new Ga4Provider(), new SseProvider()];
    this.zone.runOutsideAngular(() => {
      this.router.events.subscribe((event) => {
        if (!(event instanceof NavigationEnd)) return;
        const path = event.urlAfterRedirects || event.url;
        if (path === this.lastPath) return;
        this.lastPath = path;
        this.dispatchRoute(path, document.title);
      });
    });

    // Now boot providers. Each provider's init() returns a Promise (GA4)
    // or a synchronous value (Mixpanel). We await both so callers that
    // `await analytics.init()` see a fully-ready service.
    await Promise.all(this.providers.map((p) => Promise.resolve(p.init())));

    // Re-establish who this is BEFORE any event can fire. Without it the role
    // stays 'anonymous' for the whole session after a refresh — see
    // IDENTITY_KEY. Done after provider init so Mixpanel is ready to receive
    // the identify call, and before the first route event is dispatched.
    this.restoreIdentity();

    if (environment.analytics?.debug) {
      // console.log (not .debug) so the message is visible without needing
      // DevTools "Verbose" level enabled.
      // eslint-disable-next-line no-console
      console.log('[analytics] initialized', {
        providers: this.providers.filter((p) => p.isReady()).map((p) => p.name),
      });
    }
  }

  identify(identity: UserIdentity): void {
    if (!identity?.distinctId) {
      if (environment.analytics?.debug) {
        // eslint-disable-next-line no-console
        console.warn('[analytics] identify called without distinctId — skipping');
      }
      return;
    }
    this.currentRole = identity.role;
    this.persistIdentity(identity.distinctId, identity.role);
    this.forEachProvider((p) => p.identify(identity));
    if (environment.analytics?.debug) {
      // eslint-disable-next-line no-console
      console.log('[analytics] identify', identity);
    }
  }

  /** Remember who this is for the next page load. Never throws — analytics
   *  must not be able to break the app it measures. */
  private persistIdentity(distinctId: string, role: UserRole): void {
    try {
      localStorage.setItem(IDENTITY_KEY, JSON.stringify({ distinctId, role }));
    } catch {
      // Private mode or a full quota. The session still reports correctly;
      // only the NEXT page load falls back to 'anonymous'.
    }
  }

  /**
   * Re-apply the stored identity on boot.
   *
   * This is what stops `actor_role` reading 'anonymous' for every event after a
   * refresh. It re-identifies the providers too, not just the local field, so
   * Mixpanel's distinct_id is reasserted rather than relying on whatever its
   * own cookie happens to hold — the two must agree or events and profiles end
   * up attached to different people.
   */
  private restoreIdentity(): void {
    let stored: { distinctId?: string; role?: UserRole } | null = null;
    try {
      const raw = localStorage.getItem(IDENTITY_KEY);
      stored = raw ? JSON.parse(raw) : null;
    } catch {
      stored = null;
    }
    if (!stored?.distinctId || !stored.role || stored.role === 'anonymous') return;

    this.currentRole = stored.role;
    this.forEachProvider((p) =>
      p.identify({ distinctId: stored!.distinctId!, role: stored!.role! }));
    if (environment.analytics?.debug) {
      // eslint-disable-next-line no-console
      console.log('[analytics] identity restored', stored);
    }
  }

  /** Patch People / user properties without changing distinct_id. Use for
   *  progressive enrichment — e.g. after onboarding, set `bio_done: true`. */
  setUserProperties(props: UserProperties): void {
    this.forEachProvider((p) => p.setUserProperties(props));
    if (environment.analytics?.debug) {
      // eslint-disable-next-line no-console
      console.log('[analytics] setUserProperties', props);
    }
  }

  /** Fire an event to every registered provider. `name` is typed via the
   *  EventName union — passing an unknown string is a compile error. */
  track(name: EventName, properties?: EventProperties): void {
    if (this.shouldDrop()) return;
    const enriched = this.withActor(properties);
    this.forEachProvider((p) => p.track(name, enriched));
    if (environment.analytics?.debug) {
      // eslint-disable-next-line no-console
      console.log('[analytics] track', name, enriched);
    }
  }

  /** Manual route hook — normally you don't call this; the constructor
   *  subscribes to Router events. Useful for non-Angular route changes
   *  (e.g. modals-as-routes). */
  trackRoute(path: string, title?: string): void {
    this.dispatchRoute(path, title);
  }

  /** Detach identity. Call BEFORE localStorage.clear() so providers can
   *  still resolve the outgoing distinctId. */
  reset(): void {
    this.forEachProvider((p) => p.reset());
    this.currentRole = 'anonymous';
    // Must clear the stored copy too, or the NEXT page load restores the
    // identity this call just detached — and a logged-out visitor keeps
    // reporting as the person who last used the browser.
    try {
      localStorage.removeItem(IDENTITY_KEY);
    } catch {
      /* nothing to do — the in-memory reset above still stands */
    }
    this.lastPath = null;
    if (environment.analytics?.debug) {
      // eslint-disable-next-line no-console
      console.log('[analytics] reset');
    }
  }

  // ── Internals ────────────────────────────────────────────────────────

  private dispatchRoute(path: string, title?: string): void {
    if (this.shouldDrop()) return;
    this.forEachProvider((p) => p.trackRoute(path, title));
    if (environment.analytics?.debug) {
      // eslint-disable-next-line no-console
      console.log('[analytics] route', path, title || '');
    }
  }


  private withActor(properties?: EventProperties): EventProperties {
    return {
      ...(properties || {}),
      actor_role: this.currentRole,
      is_internal: this.currentRole === 'internal',
    };
  }
  
  /**
   * Internal staff are MUTED, not measured.
   *
   * This returned `false` unconditionally, while login.component.ts carried a
   * comment asserting the opposite ("this is what MUTES them"). So every
   * scope, search and invite an ops user performed on a client's behalf was
   * landing in the client funnel — and staff scope constantly, so the scope
   * funnel in particular was measuring StudioIP rather than its customers.
   *
   * Dropping rather than tagging is the deliberate choice: `withActor()` does
   * also stamp `is_internal`, so these events COULD be filtered in Mixpanel
   * instead. But that relies on every report remembering the filter, and a
   * funnel built without it is wrong in a way nobody notices. If you would
   * rather keep the data and filter it, this is the one line to change.
   *
   * Note the ordering dependency: this only works now that restoreIdentity()
   * re-establishes the role on boot. Previously a refreshed staff session read
   * as 'anonymous', so even a working drop would have missed most of it.
   */
  private shouldDrop(): boolean {
    return this.currentRole === 'internal';
  }

  private forEachProvider(fn: (p: AnalyticsProvider) => void): void {
    for (const p of this.providers) {
      try {
        fn(p);
      } catch (err) {
        // A single provider's throw MUST NEVER cascade to the other.
        // eslint-disable-next-line no-console
        console.warn(`[analytics/${p.name}] provider threw`, err);
      }
    }
  }
}
